import React from 'react';

import {
  FormControl,
  FormHelperText,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  useMediaQuery,
} from '@material-ui/core/';
import { SelectProps as MuiSelectProps } from '@material-ui/core/Select';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';

export type SelectOption = {
  disabled: boolean;
  label: string;
  key: string;
};

export interface SelectProps extends MuiSelectProps {
  helperText?: string;
  id: string;
  label?: string;
  menuItems?: Record<string, unknown>[] | null;
  isNative?: boolean;
  startIcon?: JSX.Element;
  selected?: SelectOption | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    width: '100%',

    '& .MuiSelect-icon': {
      fill: theme.palette.common.black,

      '&.MuiSvgIcon-root': {
        right: theme.spacing(1.5),
      },
    },

    '& .MuiSelect-nativeInput': {
      visibility: 'hidden',
    },

    '& .MuiInputAdornment-positionStart ~ .MuiSelect-nativeInput': {
      marginLeft: theme.spacing(5),
    },
  },

  list: {
    display: 'flex',
    flexDirection: 'column',

    '& option': {
      cursor: 'pointer',
      padding: theme.spacing(1.5, 2),

      '&:hover': {
        backgroundColor: theme.palette.grey[300],
      },
    },
  },

  paper: {
    marginTop: '1px',
  },

  startIcon: {
    marginRight: theme.spacing(1),
  },
}));

export function Select(props: SelectProps): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme?.breakpoints.down('xs'));

  const {
    helperText = '',
    id = '',
    isNative = isSmallScreen,
    label = '',
    menuItems = [],
    startIcon = null,
    selected = null,
    ...rest
  } = props;

  const renderSelectOption = (config, isNativeOption): JSX.Element => {
    if (isNativeOption) {
      return (
        <option disabled={config.disabled} key={config.key} value={config.key}>
          {config.label}
        </option>
      );
    }

    return (
      <MenuItem disabled={config.disabled} key={config.key} value={config.key}>
        {config.label}
      </MenuItem>
    );
  };

  const hasOptions = !!Object.keys(menuItems).length;

  const startIconElement = (
    <InputAdornment position="start">{startIcon}</InputAdornment>
  );

  const menuPaperStyles = clsx('fd-select__dropdown', classes.paper);
  const displayValue = selected ? selected.label : '';
  const selectedOrDefaultOption = selected ? selected.key : '';

  const selectStyles = clsx(classes.formControl, 'fd-select');

  return (
    <FormControl className={selectStyles}>
      {!!label && <InputLabel htmlFor={`select-${label}`}>{label}</InputLabel>}

      <MuiSelect
        disabled={!hasOptions}
        input={
          <Input
            placeholder={displayValue}
            {...(startIcon && { startAdornment: startIconElement })}
            value={displayValue}
          />
        }
        inputProps={{
          id: `select-${id}`,
        }}
        MenuProps={{
          anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom',
          },
          classes: { list: classes.list, paper: menuPaperStyles },
          disablePortal: false,
          disableScrollLock: true,
          getContentAnchorEl: null,
        }}
        name={id}
        native={isNative}
        value={selectedOrDefaultOption}
        {...rest}
      >
        {menuItems.map((config) => renderSelectOption(config, isNative))}
      </MuiSelect>

      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
